import React, { useState, useEffect, useCallback } from "react";

import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Progress,
  Button,
  Space,
  Typography,
  DatePicker,
  //Spin,
  Alert,
} from "antd";
import {
  UserOutlined,
  UserSwitchOutlined,
  BarChartOutlined,
  CrownOutlined,
  ProductOutlined,
  UserAddOutlined,
  BorderlessTableOutlined,
  EuroOutlined,
  BankOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  DeliveredProcedureOutlined,
  TruckOutlined,
  PieChartOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import CountUp from "react-countup";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";
import { useShopRoutes } from "../../hooks/useShopRoutes";
import { formatPrice } from '../../utils/priceUtils';
import { useCurrency } from '../../hooks/useCurrency';
import dayjs from "dayjs";
import "dayjs/locale/fr";
import "dayjs/locale/en"
import isoWeek from "dayjs/plugin/isoWeek";
import { useDispatch, useSelector } from "react-redux";
import { fetchDashboard } from "../../core/redux/dashboard.slice";
import { useShopId } from "../../hooks/useShopId";
import LoadingState from "../../core/common/LoadingState";
import { useTranslation } from "react-i18next";
dayjs.extend(isoWeek);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const Dashboard = () => {
  const route = useShopRoutes();
  const dispatch = useDispatch();
  const { shopId, isAllShops } = useShopId();
  const { t, i18n } = useTranslation();
  const currency = useCurrency();

  useEffect(() => {
    dayjs.locale(i18n.language === "fr" ? "fr" : "en");
  }, [i18n.language]);
  const { dashboard: db, loading, error } =
    useSelector((state) => state.dashboardSlice || {});

  const formatRangeLabel = (start, end) => {
    if (!start || !end) return t("dashboard.noPeriod");
    const fmt = (d) => dayjs(d).format("ddd DD MMM YYYY");
    return t("dashboard.periodFromTo", {
      start: fmt(start),
      end: fmt(end),
    });
  };
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);
  const [activePreset, setActivePreset] = useState("today"); // today | week | month | year | custom

  const [pendingRange, setPendingRange] = useState([
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);



  const loadDashboard = useCallback(
    (range, preset) => {
      if (!range || !range[0] || !range[1]) return;

      dispatch(
        fetchDashboard({
          shopId: isAllShops ? null : shopId,
          startDate: range[0].toISOString(),
          endDate: range[1].toISOString(),
          preset,
        })
      );
      // console.log(db); // évite de logger db ici, il sera potentiellement stale
    },
    [dispatch, isAllShops, shopId] // dépendances réelles du callback
  );

  useEffect(() => {
    loadDashboard(dateRange, activePreset);

  }, [loadDashboard, activePreset, dateRange]);

  const handlePreset = (preset) => {
    let start;
    let end = dayjs().endOf("day");

    switch (preset) {
      case "today":
        start = dayjs().startOf("day");
        break;
      case "week":
        start = dayjs().startOf("week");
        break;
      case "month":
        start = dayjs().startOf("month");
        break;
      case "year":
        start = dayjs().startOf("year");
        break;
      default:
        start = dateRange?.[0] || dayjs().startOf("day");
    }

    const newRange = [start, end];
    setDateRange(newRange);
    setPendingRange(newRange);
    setActivePreset(preset);
    loadDashboard(newRange, preset);
  };

  const handleRangeChange = (values) => {
    setPendingRange(values);
  };

  // fonction qui déduit le preset à partir des dates saisies
  const detectPresetFromRange = (rawRange) => {
    if (!rawRange || !rawRange[0] || !rawRange[1]) return "custom";

    const rawStart = rawRange[0];
    const rawEnd = rawRange[1];

    // on calcule le diff sur les dates "telles que choisies"
    const diffDays = rawEnd.endOf("day").diff(rawStart.startOf("day"), "day") + 1;

    if (diffDays === 1) return "today";
    if (diffDays <= 7) return "week";
    if (diffDays <= 30) return "month";
    return "year";
  };

  const handleApplyRange = () => {
    if (!pendingRange || !pendingRange[0] || !pendingRange[1]) return;

    // preset basé sur les valeurs brutes du picker
    const preset = detectPresetFromRange(pendingRange);

    // plage réellement appliquée = journée complète en local
    const start = pendingRange[0].startOf("day");
    const end = pendingRange[1].endOf("day");

    setDateRange([start, end]);
    setActivePreset(preset);

    loadDashboard([start, end], preset); // dans loadDashboard : toISOString()
  };

  const periodLabel = formatRangeLabel(dateRange?.[0], dateRange?.[1]);

  const chartSeries = Array.isArray(db?.monthlySeries)
    ? [...db.monthlySeries]   // on clone
      .reverse()            // on inverse l'ordre: N-1 à gauche, N à droite
      .map(s => ({
        name: s.name,
        data: [...s.data],
      }))
    : [];

  const chartOptions = {
    series: chartSeries,
    colors: ["$primary", "$secondary"],
    chart: {
      type: "bar",
      height: 320,
      stacked: false,
      zoom: { enabled: true },
    },

    responsive: [
      {
        breakpoint: 576,
        options: {
          legend: {
            position: "bottom",
            offsetY: 0,
          },
        },
      },
    ],
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 4,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "all",
        columnWidth: "35%",
      },
    },
    dataLabels: { enabled: false },
    yaxis: {
      tickAmount: 5,
    },
    xaxis: {
      categories: [
        t("months.january"),
        t("months.february"),
        t("months.march"),
        t("months.april"),
        t("months.may"),
        t("months.june"),
        t("months.july"),
        t("months.august"),
        t("months.september"),
        t("months.october"),
        t("months.november"),
        t("months.december")
      ],
    },
    legend: { show: true },
    fill: { opacity: 1 },
  };


  const bestSellerColumns = [
    {
      title: t("dashboard.product"),
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <Space>
          {record.image && (
            <Link to={route.productlist}>
              <img
                src={record.image}
                alt={record.name}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 4,
                  objectFit: "cover",
                }}
              />
            </Link>
          )}
          <Link to={route.productlist}>{record.sku} - {record.name}</Link>
        </Space>
      ),
    },
    {
      title: t("dashboard.tickets"),
      dataIndex: "ticketsCount",
      key: "ticketsCount",
    },
    // {
    //   title: "Qté vendue",
    //   dataIndex: "quantitySold",
    //   key: "quantitySold",
    // },

    {
      title: t("dashboard.revenueTTC"),
      dataIndex: "priceTTC",
      key: "priceTTC",
      render: (value) => formatPrice(value, currency),
    },
  ];

  const encaissementColumns = [
    {
      title: t("dashboard.type"),
      dataIndex: "paymentMethodName",
      key: "paymentMethodName",
      render: (value) => <Text strong>{value}</Text>,
    },
    {
      title: t("dashboard.amountCollected"),
      key: "montants",
      align: "right",
      render: (_, record) => {
        const current = record.montant ?? 0;
        const prev = record.montantPrev ?? 0;
        const delta = current - prev;
        const percentChange =
          prev === 0 ? null : (delta / prev) * 100;
        const color = (percentChange ?? 0) >= 0 ? "#3f8600" : "#cf1322";
        const sign = percentChange !== null && percentChange >= 0 ? "+" : "";

        return (
          <Space direction="vertical" size={2} style={{ textAlign: "right" }}>
            <Text>
              {formatPrice(current, currency)}
            </Text>

            <Text type="secondary" style={{ fontSize: 12 }}>
              Vs:{" "}
              {formatPrice(prev, currency)}{" "}
              {percentChange !== null && (
                <span style={{ color }}>
                  ({sign}
                  {percentChange.toFixed(1)} %)
                </span>
              )}
            </Text>
          </Space>
        );
      },
    },
    {
      title: t("dashboard.percentOfTotal"),
      key: "percent",
      align: "right",
      render: (_, record) => {
        const p = Number(record.percentFromTotal ?? 0);
        const pPrev = Number(record.percentFromTotalPrev ?? 0);
        const delta = Number(record.deltaPercent ?? p - pPrev);
        const color = delta >= 0 ? "#3f8600" : "#cf1322";
        const sign = delta >= 0 ? "+" : "";

        // “créatif” : 2 barres + variation en points
        return (
          <Space direction="vertical" size={2} style={{ textAlign: "right" }}>
            <Space size={8}>
              <div style={{ width: 90 }}>
                <Progress
                  percent={p}
                  size="small"
                  showInfo={false}
                  strokeColor="#1677ff"
                />
              </div>
              <Text>{p.toFixed(1)} %</Text>
            </Space>
            {/* <Space size={8}>
              <div style={{ width: 90 }}>
                <Progress
                  percent={pPrev}
                  size="small"
                  showInfo={false}
                  strokeColor="#bfbfbf"
                />
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Vs {pPrev.toFixed(1)} %
              </Text>
            </Space> */}
            <Text style={{ fontSize: 12, color }}>
              {sign}
              {delta.toFixed(1)} pts
            </Text>
          </Space>
        );
      },

    },
  ];


  const formatter = (value) => (

    <CountUp end={Number(value) || 0} separator=" " decimals={3} />
  );

  const renderPrevWithVariation = (current, prev, suffix = "") => {
    if (prev === null || prev === undefined) {
      return (
        <Text type="secondary" style={{ fontSize: 12 }}>
          --
        </Text>
      );
    }

    const diff = current - prev;
    const variation = prev === 0 ? null : (diff / prev) * 100;

    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {prev}
        {suffix}
        {variation !== null && (
          <>
            {" "}
            (
            <span
              style={{
                color: variation >= 0 ? "#3f8600" : "#cf1322",
              }}
            >
              {variation.toFixed(1)}%
            </span>
            )
          </>
        )}
      </Text>
    );
  };

  const getPreviousPeriodRange = () => {
    const [start, end] = dateRange || [];
    if (!start || !end) return { start: null, end: null };

    if (activePreset === "today") {
      const isoW = start.isoWeek();
      const isoD = start.isoWeekday();
      const prev = dayjs()
        .year(start.year() - 1)
        .isoWeek(isoW)
        .isoWeekday(isoD);
      return { start: prev.startOf("day"), end: prev.endOf("day") };
    }

    if (activePreset === "week") {
      const isoW = start.isoWeek();
      const year = start.year();
      const prevYear = year - 1;

      // même weekday que start, même isoWeek, année N-1
      const prevStart = dayjs()
        .year(prevYear)
        .isoWeek(isoW)
        .isoWeekday(start.isoWeekday());

      // même weekday que end, même isoWeek, année N-1
      const prevEnd = dayjs()
        .year(prevYear)
        .isoWeek(isoW)
        .isoWeekday(end.isoWeekday());

      return { start: prevStart, end: prevEnd };
    }

    if (activePreset === "month") {
      const startOfMonth = start.startOf("month");
      const prevStart = startOfMonth.subtract(1, "year");
      const prevEnd = end.subtract(1, "year");
      return { start: prevStart, end: prevEnd };
    }

    if (activePreset === "year") {
      const prevStart = start.subtract(1, "year");
      const prevEnd = end.subtract(1, "year");
      return { start: prevStart, end: prevEnd };
    }

    return {
      start: start.subtract(1, "year"),
      end: end.subtract(1, "year"),
    };
  };

  const previousRange = getPreviousPeriodRange();
  const previousPeriodLabel = formatRangeLabel(
    previousRange.start,
    previousRange.end
  );

  const startYear = dateRange?.[0]?.year();
  const prevStartYear = previousRange.start?.year();


  if (loading && !db) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <LoadingState type="section" message={t("common.loadingData")} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Alert type="error" message={error} />
        </div>
      </div>
    );
  }

  if (!db) return null;

  return (
    <div className="page-wrapper">
      <div className="content">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={16}>
            <Space wrap>
              <Button
                type={activePreset === "today" ? "primary" : "default"}
                onClick={() => handlePreset("today")}
                disabled={loading}
              >
                {t("dashboard.today")}
              </Button>
              <Button
                type={activePreset === "week" ? "primary" : "default"}
                onClick={() => handlePreset("week")}
                disabled={loading}
              >
                {t("dashboard.week")}
              </Button>
              <Button
                type={activePreset === "month" ? "primary" : "default"}
                onClick={() => handlePreset("month")}
                disabled={loading}
              >
                {t("dashboard.month")}
              </Button>
              <Button
                type={activePreset === "year" ? "primary" : "default"}
                onClick={() => handlePreset("year")}
                disabled={loading}
              >
                {t("dashboard.year")}
              </Button>
            </Space>
          </Col>
          <Col xs={24} md={8}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 4,
                alignItems: "center",
              }}
            >
              <label>{t("dashboard.period")}: </label>
              <RangePicker
                value={pendingRange}
                onChange={handleRangeChange}
                format="DD/MM/YYYY"
                placeholder={[t("common.from"), t("common.to")]}
                style={{ width: "100%" }}
                disabled={loading}
              />
              <Button
                type="primary"
                onClick={handleApplyRange}
                disabled={loading || !pendingRange || !pendingRange[0] || !pendingRange[1]}
              >
                {t("common.apply")}
              </Button>
            </div>
          </Col>
          <Col xs={24} md={16}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 4,
                alignItems: "center",
              }}
            >
              <h5>{periodLabel} </h5>
              <Text type="secondary">Vs {previousPeriodLabel}</Text>
            </div>
          </Col>
        </Row>
        {loading ? (
          <LoadingState
            type="section"
            message={t("common.loadingData")}

          />
        ) : !db ? null : (
          <>
            {/* CA / TVA / tickets / panier */}
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <EuroOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.revenueTTC")}
                        value={db.caTTC}
                        precision={3}
                        suffix={` ${currency.symbol}`}
                        formatter={formatter}
                      />
                      {renderPrevWithVariation(
                        db.caTTC,
                        db.caTTCPrev,
                        ` ${currency.symbol}`
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <BankOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.totalTVA")}
                        value={db.totalTVA}
                        precision={3}
                        suffix={` ${currency.symbol}`}
                        formatter={formatter}
                      />
                      {renderPrevWithVariation(
                        db.totalTVA,
                        db.totalTVAPrev,
                        ` ${currency.symbol}`
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <BorderlessTableOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic title={t("dashboard.ticketsCount")} value={db.ticketsCount} />
                      {renderPrevWithVariation(
                        db.ticketsCount,
                        db.ticketsCountPrev
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <ShoppingCartOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.avgBasket")}
                        value={db.panierMoyen}
                        precision={3}
                        suffix={` ${currency.symbol}`}
                        formatter={formatter}
                      />
                      {renderPrevWithVariation(
                        db.panierMoyen,
                        db.panierMoyenPrev,
                        ` ${currency.symbol}`
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
            </Row>

            {/* Commandes */}
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <ShoppingOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.ordersReceived")}
                        value={db.commandesRecues}
                      />
                      {renderPrevWithVariation(
                        db.commandesRecues,
                        db.commandesRecuesPrev
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <TruckOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.ordersToDeliver")}
                        value={db.commandesALivrer}
                      />
                      {renderPrevWithVariation(
                        db.commandesALivrer,
                        db.commandesALivrerPrev
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <DeliveredProcedureOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.ordersDelivered")}
                        value={db.commandesLivrees}
                      />
                      {renderPrevWithVariation(
                        db.commandesLivrees,
                        db.commandesLivreesPrev
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <PieChartOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.ordersNotDeliveredPercent")}
                        value={db.commandesNonLivreesPercent}
                        precision={2}
                        suffix="%"
                      />
                      {renderPrevWithVariation(
                        db.commandesNonLivreesPercent,
                        db.commandesNonLivreesPercentPrev,
                        " %"
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
            </Row>

            {/* Graph + Best sellers */}
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} lg={14}>
                <Card
                  title={
                    <Space>
                      <BarChartOutlined />
                      <span>{t("dashboard.revenueTTC")}</span>
                    </Space>
                  }
                  extra={
                    <Space>
                      <b>{startYear}</b> vs {prevStartYear}

                    </Space>
                  }
                >
                  <Chart
                    options={chartOptions}
                    series={chartSeries}
                    type="bar"
                    height={340}
                  />
                </Card>
              </Col>

              <Col xs={24} lg={10}>
                <Card
                  title={
                    <Space>
                      <CrownOutlined />
                      <Title level={4} style={{ margin: 0 }}>
                        {t("dashboard.bestSellers")}
                      </Title>
                    </Space>
                  }
                  extra={
                    <Space>
                      <b>{periodLabel}</b>
                    </Space>
                  }
                >
                  <Table
                    columns={bestSellerColumns}
                    dataSource={Array.isArray(db.bestSellers) ? db.bestSellers : []}
                    pagination={false}
                    rowKey="id"
                    size="small"
                    height={340}
                  />
                </Card>
              </Col>
            </Row>

            {/* Clients / employés */}
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <UserOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic title={t("dashboard.clients")} value={db.clientsCount} />
                      {renderPrevWithVariation(
                        db.clientsCount,
                        db.clientsCountPrev
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <UserAddOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.newClientsPercent")}
                        value={db.newClientsPercent}
                        precision={2}
                        suffix="%"
                      />
                      {renderPrevWithVariation(
                        db.newClientsPercent,
                        db.newClientsPercentPrev,
                        " %"
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <ProductOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.productsOnSale")}
                        value={db.productsOnSaleCount}
                      />
                      {renderPrevWithVariation(
                        db.productsOnSaleCount,
                        db.productsOnSaleCountPrev
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <UserSwitchOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic title={t("dashboard.employees")} value={db.employeesCount} />
                      {renderPrevWithVariation(
                        db.employeesCount,
                        db.employeesCountPrev
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
            </Row>

            {/* Encaissements synthèse */}
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <EuroOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.totalCashIn")}
                        value={db.totalEncaissement}
                        precision={3}
                        suffix={` ${currency.symbol}`}
                        formatter={formatter}
                      />
                      {renderPrevWithVariation(
                        db.totalEncaissement,
                        db.totalEncaissementPrev,
                        ` ${currency.symbol}`
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <BankOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.totalCredits")}
                        value={db.totalCredits}
                        precision={3}
                        suffix={` ${currency.symbol}`}
                        formatter={formatter}
                      />
                      {renderPrevWithVariation(
                        db.totalCredits,
                        db.totalCreditsPrev,
                        ` ${currency.symbol}`
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <CreditCardOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.supplierPayments")}
                        value={db.totalPaymentsFournisseurs}
                        precision={3}
                        suffix={` ${currency.symbol}`}
                        formatter={formatter}
                      />
                      {renderPrevWithVariation(
                        db.totalPaymentsFournisseurs,
                        db.totalPaymentsFournisseursPrev,
                        ` ${currency.symbol}`
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Space>
                    <CreditCardOutlined style={{ fontSize: 24 }} />
                    <div>
                      <Statistic
                        title={t("dashboard.supplierCredits")}
                        value={db.totalCreditsFournisseurs}
                        precision={3}
                        suffix={` ${currency.symbol}`}
                        formatter={formatter}
                      />
                      {renderPrevWithVariation(
                        db.totalCreditsFournisseurs,
                        db.totalCreditsFournisseursPrev,
                        ` ${currency.symbol}`
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
            </Row>

            {/* Encaissements détail */}
            <Card style={{ marginTop: 16 }}>
              <Space
                style={{
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Space>
                  <CreditCardOutlined style={{ fontSize: 22 }} />
                  <Title level={4} style={{ margin: 0 }}>{t("dashboard.paymentDetails")}</Title>
                </Space>

                <Text>
                  <b>{periodLabel}</b> Vs {previousPeriodLabel}
                </Text>
              </Space>

              <Table
                columns={encaissementColumns}
                dataSource={Array.isArray(db.encaissements) ? db.encaissements : []}
                rowKey="id"
                size="small"
              />
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
